/* eslint-disable no-param-reassign */
/* global G */
import { pipe } from 'lib/util'

/**
 * Resets (clears) the error and errorText properties from `item`.
 *
 * @param {DynamicFieldConfig} item dynamic item to check
 * @param {any} value               current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const clear = ({ item, value }) => {
  const newItem = { ...item, error: false, errorText: '' }
  return { item: newItem, value }
}

/**
 * Validates the `required` condition by checking that `value` is defined.
 *
 * @param {DynamicFieldConfig} item dynamic item to check
 * @param {any} value               current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const required = ({ item, value }) => {
  const failed = item.required && (!value && value !== 0)
  const errorText = failed ? item.requiredError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validates the `minLength` condition by checking that `value` is longer.
 *
 * @param {TextFieldConfig} item  dynamic item to check
 * @param {any} value             current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const minLength = ({ item, value }) => {
  const failed = !!value && item.minLength && value.length < item.minLength
  const errorText = failed ? item.minLengthError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validates the `maxLength` condition by checking that `value` is shorter.
 *
 * @param {TextFieldConfig} item  dynamic item to check
 * @param {any} value             current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const maxLength = ({ item, value }) => {
  const failed = !!value && item.maxLength && value.length > item.maxLength
  const errorText = failed ? item.maxLengthError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validates the `max` condition by checking that `value` is greater than it.
 *
 * @param {NumberFieldConfig} item  dynamic item to check
 * @param {any} value               current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const min = ({ item, value }) => {
  const failed = (item.min || item.min === 0) && value < item.min
  const errorText = failed ? item.minError || item.rangeError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validates the `max` condition by checking that `value` is lower than it.
 *
 * @param {NumberFieldConfig} item  dynamic item to check
 * @param {any} value               current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const max = ({ item, value }) => {
  const failed = (item.max || item.max === 0) && value > item.max
  const errorText = failed ? item.maxError || item.rangeError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validates the `regex` condition by checking whether `value` matches its definition.
 *
 * @param {TextFieldConfig} item  dynamic item to check
 * @param {any} value             current item's value
 * @returns {{item: (*&{error: boolean, errorText: string}), value}}
 */
const regex = ({ item, value }) => {
  const failed = !!value && item.regex && !new RegExp(item.regex).test(value)
  const errorText = failed ? item.regexError : item.errorText
  const error = failed || item.error
  const newItem = { ...item, error, errorText }
  return { item: newItem, value }
}

/**
 * Validators used for each type.
 */
const validators = {
  text: pipe(clear, required, regex, minLength, maxLength),
  check: pipe(clear, required),
  radio: pipe(clear, required),
  number: pipe(clear, required, min, max),
  select: pipe(clear, required),
  date: pipe(clear, required),
  time: pipe(clear, required),
}

/**
 * Validates `items` by using the corresponder `validators` item, according to their `type`
 * and validation properties.
 *
 * @param {DynamicItemConfig[]} items validation targets
 * @param {DynamicItemValue} value     items' values
 * @returns {*}
 */
const validateItems = (items = [], value = {}) => items
  .reduce((acc, item) => {
    if (item.hidden) {
      acc.items = [...acc.items, item]
    } else {
      const validate = validators[item.type]
      const itemValue = value?.[item.key]
      let newItem = item

      if (newItem?.items) {
        const { items: validatedItems, error } = validateItems(newItem.items, itemValue)
        acc.error ||= error
        newItem = validatedItems ? { ...newItem, items: validatedItems } : newItem
      }

      if (validate) {
        const { item: validatedItem } = validate({ item: newItem, value: itemValue })
        acc.error ||= validatedItem.error
        acc.items = [...acc.items, validatedItem]
      } else {
        acc.items = [...acc.items, newItem]
      }
    }
    return acc
  }, { items: [], error: false })

/**
 * Validates the current state of a {@link DynamicForm} component.
 *
 * @param {Gaia.Model} obj                  the model used to validate the view
 * @returns {function(*, *): Promise<*[]>}
 */
export default obj => async (uiState, key) => {
  const { items = [], value = {} } = uiState
  const { options } = obj[G.CONFIGURATION].validator[key]
  const { error: errorMessage } = options

  const { items: validatedItems, error } = validateItems(items, value)

  uiState.items = validatedItems

  if (error) {
    throw TypeError(errorMessage)
  }

  return [uiState, key]
}
